import type {ViewerMode} from '@puppycad/cad-core';

/**
 * 草图会话 ViewerMode 稳定值集合。
 *
 * 设计说明：
 * 退回控制棒会改变 FeatureManager 重建序列。只要仍处于草图环境（退出草图按钮可见），
 * 用户都还在编辑草图会话上下文内，此时不应允许通过控制棒触发模型回退重放。
 */
const SKETCH_SESSION_VIEWER_MODES = [
    'sketch',
    'sketch-drawing',
    'sketch-trim',
    'sketch-move-entities',
    'sketch-copy-entities',
    'sketch-rotate-entities',
    'sketch-circular-pattern',
    'sketch-mirror-entities',
] as const;

/**
 * 判断 Viewer 模式是否属于草图会话。
 *
 * @param viewerMode 当前 Viewer 模式。
 * @returns 草图会话模式返回 `true`。
 */
function isSketchSessionViewerMode(viewerMode: ViewerMode | string): boolean {
    return SKETCH_SESSION_VIEWER_MODES.some((sketchMode) => sketchMode === viewerMode);
}

/**
 * FeatureManager 退回控制棒禁用状态输入。
 *
 * 文件定位：
 * 1. 当前文件只负责把草图绘制状态归一为退回控制棒交互策略；
 * 2. UI 组件和动作层共同消费该纯函数，避免在多个入口重复判断；
 * 3. 退回控制棒的真实模型移动仍由 cad-core Viewer 门面完成。
 */
export interface FeatureRollbackBarDisabledInput {
    /** 当前 Viewer 模式。 */
    readonly viewerMode: ViewerMode;
    /** 是否处于草图绘制命令过程中。 */
    readonly isDrawingMode: boolean;
}

/**
 * 判断退回控制棒当前是否应禁用。
 *
 * 设计说明：
 * 草图绘制过程中底层草图命令尚未闭合，此时移动退回控制棒会改变 FeatureManager
 * 重建序列并影响未提交草图会话。这里把禁用规则收口为纯函数，让视觉、拖拽、
 * 键盘和菜单动作使用同一语义。
 *
 * @param input 当前草图与 Viewer 状态。
 * @returns 处于草图会话或草图绘制过程时返回 `true`。
 */
export function resolveFeatureRollbackBarDisabled(
    input: FeatureRollbackBarDisabledInput,
): boolean {
    return input.isDrawingMode || isSketchSessionViewerMode(input.viewerMode);
}
